import { useEffect, useRef } from 'react'
import { LineStyle } from 'lightweight-charts'
import { DEFAULT_ICT_CONFIG } from './ICTConfigPanel'
import { drawAdaptiveText, getContrastColor } from '../../utils/contrast'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hexToRgba(hex, alpha) {
  if (!hex || hex === 'transparent') return 'transparent'
  if (hex.startsWith('rgba(') || hex.startsWith('rgb(')) return hex
  const h = hex.startsWith('#') ? hex.slice(1) : hex
  if (h.length < 6) return hex
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const a = alpha !== undefined ? alpha : (h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1)
  return `rgba(${r},${g},${b},${a.toFixed(3)})`
}

function applyDash(ctx, style) {
  if (style === 'dashed') ctx.setLineDash([6, 3])
  else if (style === 'dotted') ctx.setLineDash([2, 2])
  else ctx.setLineDash([])
}

function pill(ctx, text, x, y, color, chartBackground) {
  ctx.font = 'bold 8.5px ui-sans-serif,system-ui,sans-serif'
  drawAdaptiveText(ctx, text, x, y, color, chartBackground, 'left', 'alphabetic')
}

function rrect(ctx, x, y, w, h, r) {
  const R = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + R, y)
  ctx.lineTo(x + w - R, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + R)
  ctx.lineTo(x + w, y + h - R)
  ctx.quadraticCurveTo(x + w, y + h, x + w - R, y + h)
  ctx.lineTo(x + R, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - R)
  ctx.lineTo(x, y + R)
  ctx.quadraticCurveTo(x, y, x + R, y)
  ctx.closePath()
}

function box(ctx, x, y, w, h, fill, stroke, strokeW = 1) {
  ctx.fillStyle = fill
  ctx.fillRect(x, y, w, h)
  if (stroke) {
    ctx.strokeStyle = stroke
    ctx.lineWidth = strokeW
    ctx.setLineDash([])
    ctx.strokeRect(x, y, w, h)
  }
}

function freshnessAlpha(baseAlpha, leftMs, maxRightMs, decayBars = 60, barMsEstimate = 300_000) {
  if (!maxRightMs || maxRightMs <= leftMs) return baseAlpha
  const barsOld = Math.max(0, (maxRightMs - leftMs) / barMsEstimate)
  return Math.max(0.12, baseAlpha * Math.exp(-barsOld / decayBars))
}

function drawGlowLine(ctx, x1, y, x2, color, width, glowAlpha = 0.55, blur = 8) {
  ctx.save()
  ctx.strokeStyle = 'transparent'
  ctx.lineWidth = width
  ctx.shadowColor = hexToRgba(color, glowAlpha)
  ctx.shadowBlur = blur
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 0
  ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke()
  ctx.restore()
}

function parseRgbaAlpha(rgba) {
  const m = rgba.match(/rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+)\s*)?\)/)
  return m && m[1] ? parseFloat(m[1]) : 1
}

/**
 * ICTCanvas — canvas overlay for the Smart Money Engine output.
 * Props: chartRef, seriesRef, data (SmeOutput), config (ICT config), signal, historySignals
 */
export default function ICTCanvas({ chartRef, seriesRef, data, config, signal, historySignals, chartVersion, chartBackground = '#111827' }) {
  const canvasRef = useRef(null)
  const priceLinesRef = useRef([])
  const cfg = config ?? DEFAULT_ICT_CONFIG

  useEffect(() => {
    const canvas = canvasRef.current
    const chart  = chartRef.current
    const series = seriesRef.current
    if (!canvas || !chart || !series || !data) return

    const dpr = window.devicePixelRatio || 1

    // ── Price lines nativas del eje para entry/SL/TP ────────────────────────
    // Antes este texto (SL/TP1.../BE) se dibujaba a mano en el canvas, pegado
    // al borde derecho — el mismo espacio de píxeles que ocupan los propios
    // números del eje de precio, tapándolos. createPriceLine() delega el
    // renderizado del tag al eje, que ya sabe convivir con sus ticks (así es
    // como TradingView y el resto de charts basados en esta librería lo
    // resuelven) y además se reposiciona solo al hacer zoom/pan.
    for (const pl of priceLinesRef.current) {
      try { series.removePriceLine(pl) } catch {}
    }
    priceLinesRef.current = []

    const sigCfgForLines = cfg.signal ?? {}
    const minScoreLines = sigCfgForLines.minScore ?? 4
    const minRRLines = sigCfgForLines.minRR ?? 1.2
    const sigRRLines = signal?.risk_reward_1 ?? 0
    if (
      signal &&
      signal.direction !== 'none' &&
      sigCfgForLines.showSignal !== false &&
      signal.confluence_score >= minScoreLines &&
      sigRRLines >= minRRLines
    ) {
      const isLongLines = signal.direction === 'long'
      const entryColorLines = sigCfgForLines.entryColor || '#e2e8f0'
      const slColorLines    = sigCfgForLines.slColor    || '#f87171'
      const tpColorLines    = sigCfgForLines.tpColor    || '#4ade80'
      const numTPsLines = sigCfgForLines.numTPs ?? 5

      const addPriceLine = (price, color, title, lineWidth, lineStyle) => {
        if (price == null) return
        const pl = series.createPriceLine({
          price,
          color,
          lineWidth,
          lineStyle,
          axisLabelVisible: true,
          title,
          axisLabelColor: color,
          axisLabelTextColor: getContrastColor(color),
        })
        priceLinesRef.current.push(pl)
      }

      addPriceLine(
        signal.entry_price, entryColorLines,
        `BE / ${signal.grade || (isLongLines ? 'LONG' : 'SHORT')}`, 1, LineStyle.Solid
      )
      if (sigCfgForLines.showSL !== false) {
        addPriceLine(signal.stop_loss, slColorLines, 'SL', 2, LineStyle.Solid)
      }
      if (sigCfgForLines.showTP1 !== false && numTPsLines >= 1) {
        addPriceLine(signal.take_profit_1, tpColorLines, `TP1 ${(signal.risk_reward_1 ?? 0).toFixed(1)}R`, 2, LineStyle.Dashed)
      }
      if (sigCfgForLines.showTP2 !== false && numTPsLines >= 2) {
        addPriceLine(signal.take_profit_2, tpColorLines, `TP2 ${(signal.risk_reward_2 ?? 0).toFixed(1)}R`, 1, LineStyle.Dashed)
      }
      if (sigCfgForLines.showTP3 !== false && numTPsLines >= 3) {
        addPriceLine(signal.take_profit_3, tpColorLines, `TP3 ${(signal.risk_reward_3 ?? 0).toFixed(1)}R`, 1, LineStyle.Dashed)
      }
      if (sigCfgForLines.showTP4 !== false && numTPsLines >= 4 && signal.take_profit_4 != null) {
        addPriceLine(signal.take_profit_4, tpColorLines, `TP4 ${(signal.risk_reward_4 ?? 0).toFixed(1)}R`, 1, LineStyle.SparseDotted)
      }
      if (sigCfgForLines.showTP5 !== false && numTPsLines >= 5 && signal.take_profit_5 != null) {
        addPriceLine(signal.take_profit_5, tpColorLines, `TP5 ${(signal.risk_reward_5 ?? 0).toFixed(1)}R`, 1, LineStyle.SparseDotted)
      }
    }

    const draw = () => {
      const parent = canvas.parentElement
      if (!parent) return
      const { width: w, height: h } = parent.getBoundingClientRect()
      if (w === 0 || h === 0) return

      const needW = Math.round(w * dpr)
      const needH = Math.round(h * dpr)
      if (canvas.width !== needW || canvas.height !== needH) {
        canvas.width        = needW
        canvas.height       = needH
        canvas.style.width  = w + 'px'
        canvas.style.height = h + 'px'
      }

      // willReadFrequently: drawAdaptiveText() below samples pixels back via
      // getImageData for every label drawn (often 10-50+ per frame while
      // panning/zooming); without this flag the browser keeps re-promoting
      // the canvas between GPU and CPU backing on each readback, which is
      // slow enough to show up as dropped frames.
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      // Ancho del área de ploteo (excluyendo el eje de precio) — todo lo que
      // dibujamos a mano (zonas de color, badges) se recorta a `plotW` para
      // no invadir esa franja, que ya usan los propios ticks del eje y ahora
      // también las price lines nativas de entry/SL/TP.
      //
      // chart.priceScale('right').width() devolvía 0 aquí (el relleno
      // llegaba hasta el borde real del contenedor, tapando los números del
      // eje) — timeScale().width() es la fuente fiable: la propia librería
      // fija el ancho del time-axis widget al ancho del pane principal SIN
      // contar los stubs de los ejes de precio (ver _adjustSizeImpl), que es
      // exactamente el mismo sistema de coordenadas que ya usa toX() más abajo.
      const plotW = chart.timeScale().width() || w

      const toX = (ms) => chart.timeScale().timeToCoordinate(ms / 1000)
      const toY = (price) => series.priceToCoordinate(price)

      const fc = cfg.fvg ?? {}
      const oc = cfg.ob ?? {}
      const sc = cfg.structure ?? {}
      const fibc = cfg.fib ?? {}
      const mtfCfg = cfg.multiTimeframe ?? {}
      const sigCfg = cfg.signal ?? {}

      const maxRightMs = data.timestamp || 0
      const boxX = (leftMs, rightMs) => {
        const effectiveRight = (maxRightMs > leftMs) ? Math.min(rightMs, maxRightMs) : rightMs
        let lx = toX(leftMs), rx = toX(effectiveRight)
        if (lx == null && rx == null) return null
        lx = lx ?? 0; rx = rx ?? w
        if (rx < 0 || lx > w) return null
        return [Math.max(0, lx), Math.min(w, rx)]
      }

      const boxes = data.boxes ?? []
      const lines = data.lines ?? []
      const labels = data.labels ?? []

      // ── DRAW LAYER 1: Boxes (FVG / OB / IFVG / OTE) ─────────────────────────
      for (const b of boxes) {
        const y1 = toY(b.top), y2 = toY(b.bottom)
        if (y1 == null || y2 == null) continue
        const top = Math.min(y1, y2), height = Math.abs(y2 - y1)
        if (height < 1) continue
        const xs = boxX(b.left, b.right)
        if (!xs) continue
        const [x1, x2] = xs, bw = x2 - x1

        if (b.type === 'FVG') {
          if (fc.showFVG === false) continue
          const baseColor = b.direction === 'bull' ? (fc.colorBull || '#2962FF') : (fc.colorBear || '#FF6D00')
          const fillAlpha = freshnessAlpha(fc.alphaFill ?? 0.18, b.left, maxRightMs)
          box(ctx, x1, top, bw, height, hexToRgba(baseColor, fillAlpha), hexToRgba(baseColor, 0.7), 0.5)
          if (fc.showMidline !== false && b.show_midline) {
            const my = toY(b.midline)
            if (my != null) {
              ctx.strokeStyle = hexToRgba(baseColor, 0.35)
              ctx.lineWidth = 1
              ctx.setLineDash([4, 3])
              ctx.beginPath(); ctx.moveTo(x1, my); ctx.lineTo(x2, my); ctx.stroke()
              ctx.setLineDash([])
            }
          }
          if (fc.showLabel !== false && height > 10) {
            const label = b.direction === 'bull' ? 'BISI' : 'SIBI'
            pill(ctx, label, x1 + 3, top + 9, hexToRgba(baseColor, 1), chartBackground)
          }
        } else if (b.type === 'IFVG') {
          if (fc.showIFVG === false) continue
          const baseColor = fc.colorIFVG || '#a855f7'
          const fillAlpha = freshnessAlpha(fc.alphaFill ?? 0.18, b.left, maxRightMs)
          box(ctx, x1, top, bw, height, hexToRgba(baseColor, fillAlpha * 0.6), hexToRgba(baseColor, 0.8), 0.5)
          if (height > 10) pill(ctx, 'IFVG', x1 + 3, top + 9, hexToRgba(baseColor, 1), chartBackground)
        } else if (b.type === 'OB') {
          if (oc.showOB === false) continue
          const baseColor = b.direction === 'bull' ? (oc.colorBull || '#1B5E20') : (oc.colorBear || '#FF5252')
          const fillAlpha = freshnessAlpha(oc.alphaFill ?? 0.22, b.left, maxRightMs)
          box(ctx, x1, top, bw, height, hexToRgba(baseColor, fillAlpha), hexToRgba(baseColor, 0.8), 1)
          if (oc.showMidline !== false && b.show_midline) {
            const my = toY(b.midline)
            if (my != null && my >= top && my <= top + height) {
              ctx.strokeStyle = hexToRgba(baseColor, 0.35)
              ctx.lineWidth = 1
              ctx.setLineDash([5, 3])
              ctx.beginPath(); ctx.moveTo(x1, my); ctx.lineTo(x2, my); ctx.stroke()
              ctx.setLineDash([])
            }
          }
          if (oc.showLabel !== false && height > 10) {
            const tag = `${b.timeframe} OB`
            pill(ctx, tag, x1 + 3, top + 9, hexToRgba(baseColor, 1), chartBackground)
          }
        } else if (b.type === 'OTE') {
          if (fibc.showOTE === false) continue
          const fillColor = 'rgba(211, 181, 120, 0.22)'
          const borderColor = '#B08D57'
          box(ctx, x1, top, bw, height, fillColor, borderColor, 1)
          if (height > 10) pill(ctx, 'OTE', x1 + 3, top + 9, borderColor, chartBackground)
        }
      }

      // ── DRAW LAYER 2: Lines (BOS / CHoCH / Internal / Fib / Strong-Weak) ────
      const showSwing = sc.showSwingStruct !== false
      const showInternal = sc.showInternalStruct !== false
      const showStrongWeak = sc.showStrongWeak !== false
      const showFib = fibc.showFib !== false

      for (const line of lines) {
        const y = toY(line.price)
        if (y == null || y < -5 || y > h + 5) continue
        const xs = boxX(line.start_time, line.end_time)
        if (!xs) continue
        const [x1, x2] = xs

        let visible = false
        let lineColor = line.color
        let isChoch = false
        if (line.type === 'BOS' || line.type === 'CHoCH') {
          visible = showSwing
          if (line.type === 'CHoCH') isChoch = true
        } else if (line.type === 'INTERNAL_BOS' || line.type === 'INTERNAL_CHoCH') {
          visible = showInternal
          if (line.type === 'INTERNAL_CHoCH') isChoch = true
        } else if (line.type === 'FIB') {
          visible = showFib
        } else if (line.type === 'STRONG' || line.type === 'WEAK') {
          visible = showStrongWeak
        }
        if (!visible) continue

        const lineW = line.line_width ?? 1
        if (isChoch) drawGlowLine(ctx, x1, y, x2, lineColor, lineW * 2, 0.5, 10)
        ctx.strokeStyle = hexToRgba(lineColor, 0.9)
        ctx.lineWidth = lineW
        applyDash(ctx, line.line_style)
        ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke()
        ctx.setLineDash([])
        if (line.show_label && line.label) {
          pill(ctx, line.label, x1 + 5, y - 2, lineColor, chartBackground)
        }
      }

      // ── DRAW LAYER 3: Labels (HH / HL / LH / LL) ────────────────────────────
      if (sc.showSwingLabels !== false) {
        ctx.textAlign = 'center'
        for (const lb of labels) {
          const y = toY(lb.price), x = toX(lb.time)
          if (y == null || x == null || x < -20 || x > w + 20) continue
          const isHigh = lb.style === 'label_down'
          const color = lb.text_color || (isHigh ? sc.colorBear || '#FF5252' : sc.colorBull || '#1B5E20')
          ctx.font = 'bold 8.5px ui-sans-serif,system-ui,sans-serif'
          ctx.fillStyle = hexToRgba(color, 0.85)
          ctx.beginPath()
          if (isHigh) {
            ctx.moveTo(x, y - 4)
            ctx.lineTo(x - 3, y - 9)
            ctx.lineTo(x + 3, y - 9)
          } else {
            ctx.moveTo(x, y + 4)
            ctx.lineTo(x - 3, y + 9)
            ctx.lineTo(x + 3, y + 9)
          }
          ctx.closePath(); ctx.fill()
          const oy = isHigh ? y - 22 : y + 11
          drawAdaptiveText(ctx, lb.text, x, oy + 8.5, color, chartBackground, 'center', 'alphabetic')
        }
        ctx.textAlign = 'left'
      }

      // ── DRAW LAYER 4: Active signal ─────────────────────────────────────────
      const minScore = sigCfg.minScore ?? 4
      const minRR = sigCfg.minRR ?? 1.2
      const signalRR = signal?.risk_reward_1 ?? 0
      if (
        signal &&
        signal.direction !== 'none' &&
        sigCfg.showSignal !== false &&
        signal.confluence_score >= minScore &&
        signalRR >= minRR
      ) {
        const isLong = signal.direction === 'long'
        const longColor  = sigCfg.longColor  || '#22c55e'
        const shortColor = sigCfg.shortColor || '#ef4444'
        const slColor    = sigCfg.slColor    || '#f87171'
        const tpColor    = sigCfg.tpColor    || '#4ade80'
        const gradeColor = isLong ? longColor : shortColor
        const numTPs = sigCfg.numTPs ?? 5
        const entryY = toY(signal.entry_price)
        const slY    = (sigCfg.showSL !== false) ? toY(signal.stop_loss) : null
        const tp1Y   = (sigCfg.showTP1 !== false && numTPs >= 1) ? toY(signal.take_profit_1) : null

        const signalX = signal.entry_time ? toX(signal.entry_time) : null
        const signalOnScreen = signalX == null || signalX <= w + 20
        const lineStartX = (signalOnScreen && signalX != null && signalX >= 0 && signalX < w) ? signalX : 0

        // Las líneas y sus etiquetas de SL/TP/entry ya las dibuja
        // series.createPriceLine() (ver arriba) directamente sobre el eje —
        // aquí solo queda la zona de color de riesgo/recompensa, que debe
        // ser una caja acotada pegada a la entrada (como las de OB/FVG),
        // NO una franja que se estire hasta el borde del gráfico. Se fija su
        // ancho en un número constante de velas (vía logical index, que ya
        // tiene en cuenta el zoom actual) en vez de en píxeles absolutos, así
        // se ve igual de compacta tanto muy zoomeado como muy alejado.
        const ZONE_BARS = 20
        const entryLogical = chart.timeScale().coordinateToLogical(lineStartX)
        const zoneRightRaw = entryLogical != null
          ? chart.timeScale().logicalToCoordinate(entryLogical + ZONE_BARS)
          : null
        const zoneRight = Math.min(plotW, zoneRightRaw != null ? zoneRightRaw : lineStartX)
        const flatZone = (ya, yb, color, opacity) => {
          if (ya == null || yb == null) return
          const top = Math.min(ya, yb), ht = Math.abs(yb - ya)
          if (ht < 1) return
          ctx.fillStyle = hexToRgba(color, opacity)
          ctx.fillRect(lineStartX, top, Math.max(0, zoneRight - lineStartX), ht)
        }
        if (signalOnScreen && entryY != null && slY != null) flatZone(entryY, slY, slColor, 0.18)
        if (signalOnScreen && entryY != null && tp1Y != null) flatZone(entryY, tp1Y, tpColor, 0.18)

        const grade = signal.grade || (isLong ? 'LONG' : 'SHORT')
        if (signalOnScreen && entryY != null) {
          const SZ  = 12
          const OFF = 26
          const arrowY = isLong ? entryY + OFF : entryY - OFF
          const arrowCX = signalX ?? lineStartX + 30
          ctx.fillStyle = gradeColor
          ctx.beginPath()
          if (isLong) {
            ctx.moveTo(arrowCX, arrowY - SZ)
            ctx.lineTo(arrowCX - SZ * 0.75, arrowY + SZ * 0.5)
            ctx.lineTo(arrowCX + SZ * 0.75, arrowY + SZ * 0.5)
          } else {
            ctx.moveTo(arrowCX, arrowY + SZ)
            ctx.lineTo(arrowCX - SZ * 0.75, arrowY - SZ * 0.5)
            ctx.lineTo(arrowCX + SZ * 0.75, arrowY - SZ * 0.5)
          }
          ctx.closePath(); ctx.fill()
          ctx.font = 'bold 10px ui-sans-serif,system-ui,sans-serif'
          const gw = ctx.measureText(grade).width
          const gxRaw = arrowCX + SZ + 6
          const gx = Math.min(gxRaw, plotW - gw - 8)
          const gy = arrowY + 3.5
          drawAdaptiveText(ctx, grade, gx, gy, gradeColor, chartBackground, 'left', 'alphabetic')
          const scoreTxt = `Confluencia: ${signal.confluence_score}/9`
          ctx.font = '7.5px ui-sans-serif,system-ui,sans-serif'
          const sX = gx, sY = gy - 11
          drawAdaptiveText(ctx, scoreTxt, sX, sY + 7.5, gradeColor, chartBackground, 'left', 'alphabetic')
        }
      }

      // ── DRAW LAYER 5: Historical signals ────────────────────────────────────
      const hSigs = historySignals?.signals ?? []
      if (sigCfg.showHistory !== false && hSigs.length > 0) {
        const histTpColor = sigCfg.tpColor    || '#4ade80'
        const histSlColor = sigCfg.slColor    || '#f87171'
        const histOpenColor = sigCfg.entryColor ? hexToRgba(sigCfg.entryColor, 0.7) : '#94a3b8'
        for (const hs of hSigs) {
          if (hs.confluence_score < minScore) continue
          const isLong = hs.direction === 'long'
          const hx = toX(hs.entry_time)
          if (hx == null || hx < -20 || hx > w + 20) continue
          const hy = toY(hs.entry_price)
          if (hy == null) continue
          const color = hs.outcome === 'SL' ? histSlColor : (hs.outcome === 'OPEN' ? histOpenColor : histTpColor)
          const SZ  = 5
          const OFF = 16
          const arrowY = isLong ? hy + OFF : hy - OFF
          ctx.fillStyle = hexToRgba(color, 0.85)
          ctx.beginPath()
          if (isLong) {
            ctx.moveTo(hx,            arrowY - SZ)
            ctx.lineTo(hx - SZ * 0.7, arrowY + SZ * 0.5)
            ctx.lineTo(hx + SZ * 0.7, arrowY + SZ * 0.5)
          } else {
            ctx.moveTo(hx,            arrowY + SZ)
            ctx.lineTo(hx - SZ * 0.7, arrowY - SZ * 0.5)
            ctx.lineTo(hx + SZ * 0.7, arrowY - SZ * 0.5)
          }
          ctx.closePath(); ctx.fill()
          const badgeTxt = `${hs.grade}·${hs.outcome}`
          ctx.font = 'bold 8px ui-sans-serif,system-ui,sans-serif'
          const bw = ctx.measureText(badgeTxt).width
          const bxRaw = hx + SZ + 4
          const bx = Math.min(bxRaw, plotW - bw - 4)
          const by = arrowY + 3
          drawAdaptiveText(ctx, badgeTxt, bx, by, color, chartBackground, 'left', 'alphabetic')
        }
        const stats = historySignals?.stats
        if (stats && stats.total > 0) {
          const winPct  = Math.round((stats.win_rate ?? 0) * 100)
          const line1   = `Historial ICT: ${stats.total} señales`
          const line2   = `✓ ${stats.wins} (${winPct}%)   ✗ ${stats.losses}${stats.open > 0 ? `   ? ${stats.open}` : ''}`
          ctx.font = '9px ui-sans-serif,system-ui,sans-serif'
          const mx = 8, my = h - 38
          drawAdaptiveText(ctx, line1, mx, my + 9, histTpColor, chartBackground, 'left', 'alphabetic')
          ctx.font = 'bold 9px ui-sans-serif,system-ui,sans-serif'
          drawAdaptiveText(ctx, line2, mx, my + 21, stats.win_rate >= 0.5 ? histTpColor : histSlColor, chartBackground, 'left', 'alphabetic')
          ctx.textAlign = 'left'
        }
      }

      // ── HTF bias badge ──────────────────────────────────────────────────────
      const htfBias = data?.htf_bias
      if (htfBias && htfBias.direction !== 'neutral') {
        const tf = (htfBias.timeframe || 'HTF').toUpperCase()
        const biasText = `HTF ${tf} ${htfBias.direction.toUpperCase()} ${(htfBias.strength * 100).toFixed(0)}%`
        ctx.font = 'bold 10px ui-sans-serif,system-ui,sans-serif'
        const bW = ctx.measureText(biasText).width
        const bX = plotW - bW - 8
        const bY = 38
        const biasColor = htfBias.direction === 'bull' ? '#4ade80' : '#f87171'
        drawAdaptiveText(ctx, biasText, bX, bY, biasColor, chartBackground, 'left', 'alphabetic')
        ctx.textAlign = 'left'
      }

      // ── PD position badge ───────────────────────────────────────────────────
      const pd = data?.pd_position
      if (typeof pd === 'number') {
        const pdText = `PD ${pd.toFixed(2)}`
        ctx.font = 'bold 10px ui-sans-serif,system-ui,sans-serif'
        const pdW = ctx.measureText(pdText).width
        const pdX = plotW - pdW - 8
        const pdY = 16
        const pdColor = pd > 0.6 ? '#f87171' : (pd < 0.4 ? '#4ade80' : '#94a3b8')
        drawAdaptiveText(ctx, pdText, pdX, pdY, pdColor, chartBackground, 'left', 'alphabetic')
        ctx.textAlign = 'left'
      }
    }

    // Debounce redraws via requestAnimationFrame so rapid chart events
    // (scroll, zoom, kinetic scroll) stay synced without over-painting.
    let rafId = 0
    const scheduleDraw = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        draw()
      })
    }

    const timeScale = chart.timeScale()
    // Resize is driven exclusively by the chart's own size-change event (fired
    // by lightweight-charts only after ChartRenderer's ResizeObserver has
    // actually applied the new size). A separate ResizeObserver on the same
    // container used to race with that resize and cause the overlay to draw
    // against stale coordinates for a frame, producing a visible "jump" any
    // time the layout was resized.
    timeScale.subscribeVisibleLogicalRangeChange(scheduleDraw)
    timeScale.subscribeVisibleTimeRangeChange(scheduleDraw)
    timeScale.subscribeSizeChange(scheduleDraw)
    chart.subscribeCrosshairMove(scheduleDraw)

    let ptrDown = false
    const onDocDown = () => { ptrDown = true }
    const onDocMove = () => { if (ptrDown) scheduleDraw() }
    const onDocUp   = () => { ptrDown = false; scheduleDraw() }
    document.addEventListener('pointerdown', onDocDown)
    document.addEventListener('pointermove', onDocMove)
    document.addEventListener('pointerup',   onDocUp)

    draw()

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      // Envuelto en try/catch: si el chart cambió de tipo (ChartPage/
      // ChartPublicPage remontan ChartRenderer con `key={chartType}`), el
      // chart de esta closure puede haber sido destruido ya por ese remount
      // antes de que este cleanup llegue a ejecutarse — lightweight-charts
      // lanza "Object is disposed" al llamar a cualquier método sobre él.
      try { timeScale.unsubscribeVisibleLogicalRangeChange(scheduleDraw) } catch {}
      try { timeScale.unsubscribeVisibleTimeRangeChange(scheduleDraw) } catch {}
      try { timeScale.unsubscribeSizeChange(scheduleDraw) } catch {}
      try { chart.unsubscribeCrosshairMove(scheduleDraw) } catch {}
      document.removeEventListener('pointerdown', onDocDown)
      document.removeEventListener('pointermove', onDocMove)
      document.removeEventListener('pointerup',   onDocUp)
      const c = canvasRef.current
      if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height)
      for (const pl of priceLinesRef.current) {
        try { series.removePriceLine(pl) } catch {}
      }
      priceLinesRef.current = []
    }
  }, [data, config, signal, historySignals, chartRef, seriesRef, chartVersion])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 5 }}
    />
  )
}
